import { apiPost, type RequestOptions } from './index'

export interface ClockLiveItem {
  id: number
  day_id: number
  sort_order: number
  item_type: string
  title: string | null
  planned_duration_sec: number
  auto_advance: boolean
  fixed_time: string | null
  notes: string | null
  actual_start: string | null
  actual_start_iso_utc?: string | null
  actual_start_epoch?: number | null
  actual_end: string | null
  actual_end_iso_utc?: string | null
  actual_end_epoch?: number | null
  skipped: boolean
}

export interface ClockLiveProjectionItem {
  id: number
  projected_start: string
  projected_end: string
  is_fixed: boolean
  delta_from_schedule: number
  scheduled_start?: string
  scheduled_end?: string
}

export interface ClockLiveProjection {
  items: ClockLiveProjectionItem[]
  ahead_behind_total: number
  next_fixed_at: string | null
  seconds_to_next_fixed: number | null
  next_fixed_delta: number | null
}

export interface ClockLiveWarning {
  code: string
  message: string
  context?: unknown
}

export interface ClockLiveActionResponse {
  station_id: number
  day_id: number
  day_item_id: number
  item: ClockLiveItem
  projection: ClockLiveProjection
  warning?: ClockLiveWarning | null
  ended_item?: ClockLiveItem | null
}

export interface ClockLiveReconcileResponse {
  items: ClockLiveItem[]
  projection: ClockLiveProjection
}

interface StartItemParams {
  stationId?: number | null
  confirm?: boolean
  signal?: AbortSignal
  projectedStart?: string | null
}

interface SimpleItemParams {
  stationId?: number | null
  signal?: AbortSignal
  projectedStart?: string | null
  projectedEnd?: string | null
}

interface AdjustDurationParams {
  stationId?: number | null
  plannedDurationSec: number
  signal?: AbortSignal
}

interface EstimateDayParams {
  dayId: number
  stationId?: number | null
  signal?: AbortSignal
}

export const startClockDayItem = async (
  dayItemId: number,
  params: StartItemParams = {},
): Promise<ClockLiveActionResponse> => {
  const body: Record<string, unknown> = {}
  if (params.stationId && params.stationId > 0) {
    body.station_id = params.stationId
  }
  if (params.confirm) {
    body.confirm = params.confirm
  }
  if (typeof params.projectedStart === 'string' && params.projectedStart.trim().length > 0) {
    body.projected_start = params.projectedStart
  }
  return apiPost<ClockLiveActionResponse>(`clock/live/${dayItemId}/start`, body, {
    signal: params.signal,
  })
}

export const endClockDayItem = async (
  dayItemId: number,
  params: SimpleItemParams = {},
): Promise<ClockLiveActionResponse> => {
  const body: Record<string, unknown> = {}
  if (params.stationId && params.stationId > 0) {
    body.station_id = params.stationId
  }
  if (typeof params.projectedStart === 'string' && params.projectedStart.trim().length > 0) {
    body.projected_start = params.projectedStart
  }
  if (typeof params.projectedEnd === 'string' && params.projectedEnd.trim().length > 0) {
    body.projected_end = params.projectedEnd
  }
  return apiPost<ClockLiveActionResponse>(`clock/live/${dayItemId}/end`, body, {
    signal: params.signal,
  })
}

export const skipClockDayItem = async (
  dayItemId: number,
  params: SimpleItemParams = {},
): Promise<ClockLiveActionResponse> => {
  const body: Record<string, unknown> = {}
  if (params.stationId && params.stationId > 0) {
    body.station_id = params.stationId
  }
  return apiPost<ClockLiveActionResponse>(`clock/live/${dayItemId}/skip`, body, {
    signal: params.signal,
  })
}

export const adjustClockDayItemDuration = async (
  dayItemId: number,
  params: AdjustDurationParams,
): Promise<ClockLiveActionResponse> => {
  const body: Record<string, unknown> = {
    planned_duration_sec: Math.max(0, Math.round(params.plannedDurationSec)),
  }
  if (params.stationId && params.stationId > 0) {
    body.station_id = params.stationId
  }
  return apiPost<ClockLiveActionResponse>(`clock/live/${dayItemId}/adjust-duration`, body, {
    signal: params.signal,
  })
}

interface EnforceFixedAnchorParams {
  dayId: number
  stationId?: number | null
  anchorItemId?: number | null
  signal?: AbortSignal
}

export const enforceClockFixedAnchor = async (
  params: EnforceFixedAnchorParams,
): Promise<ClockLiveActionResponse> => {
  const body: Record<string, unknown> = {}
  if (params.stationId && params.stationId > 0) {
    body.station_id = params.stationId
  }
  if (params.anchorItemId && params.anchorItemId > 0) {
    body.anchor_item_id = params.anchorItemId
  }
  return apiPost<ClockLiveActionResponse>(`clock/live/${params.dayId}/enforce-fixed-anchor`, body, {
    signal: params.signal,
  })
}

interface AutoAdvanceParams {
  stationId?: number | null
  projectedStart?: string | null
  signal?: AbortSignal
}

export const autoAdvanceClockDayItem = async (
  dayItemId: number,
  params: AutoAdvanceParams = {},
): Promise<ClockLiveActionResponse> => {
  const body: Record<string, unknown> = {}
  if (params.stationId && params.stationId > 0) {
    body.station_id = params.stationId
  }
  if (typeof params.projectedStart === 'string' && params.projectedStart.trim().length > 0) {
    body.projected_start = params.projectedStart
  }
  return apiPost<ClockLiveActionResponse>(`clock/live/${dayItemId}/auto-advance`, body, {
    signal: params.signal,
  })
}

interface ReconcileParams {
  dayId: number
  stationId?: number | null
  currentItemId?: number | null
  signal?: AbortSignal
}

export const reconcileClockDay = async (params: ReconcileParams): Promise<ClockLiveReconcileResponse> => {
  const body: Record<string, unknown> = {}
  if (params.stationId && params.stationId > 0) {
    body.station_id = params.stationId
  }
  if (params.currentItemId && params.currentItemId > 0) {
    body.current_item_id = params.currentItemId
  }
  return apiPost<ClockLiveReconcileResponse>(`clock/live/${params.dayId}/reconcile`, body, {
    signal: params.signal,
  })
}

export const estimateClockDay = async (
  params: EstimateDayParams,
  options?: RequestOptions,
): Promise<ClockLiveProjection> => {
  const body: Record<string, unknown> = { day_id: params.dayId }
  if (params.stationId && params.stationId > 0) {
    body.station_id = params.stationId
  }
  return apiPost<ClockLiveProjection>('clock/estimate', body, {
    signal: params.signal ?? options?.signal,
  })
}
